// web/src/pages/ResultsPage.jsx
import React, { useEffect, useState, Suspense } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../contexts/AuthContext";
// import { useConsent } from "../../contexts/ConsentContext";
import { DEPT_DICT, extractDeptFromInsee, deriveCommuneName } from "../../utils/geo";
import { nfmt, nfmt2 } from "../../utils/format";
import FavoriteButton from "../../utils/FavoriteButton";
import exampleFull from "../../assets/examples/example_full.json";
import HeaderAuth from "../../components/common/HeaderAuth";
import SiteFooter from "../../components/common/SiteFooter";
import AdSenseBanner from "../../components/ads/AdSenseBanner";
import logo from "../../assets/logo-white-bg.png";
import { decodeBanIdFromUrl, encodeBanIdForUrl } from "../../utils/resultUrl";

// lazy-load sections
const AdresseSection = React.lazy(() => import("../../components/sections/AdresseSection"));
const ImmobilierSection = React.lazy(() => import("../../components/sections/ImmobilierSection"));
const MeteoSection = React.lazy(() => import("../../components/sections/MeteoSection"));
const ClimatSection = React.lazy(() => import("../../components/sections/ClimatSection"));
const EnvironnementSection = React.lazy(() => import("../../components/sections/EnvironnementSection"));
const RisksSection = React.lazy(() => import("../../components/sections/RisksSection"));
const ServicesSection = React.lazy(() => import("../../components/sections/ServicesSection"));
const CultureSection = React.lazy(() => import("../../components/sections/CultureSection"));
const TransportsSection = React.lazy(() => import("../../components/sections/TransportsSection"));
const SourcesSection = React.lazy(() => import("../../components/sections/SourcesSection"));

const ADSENSE_RESULTS_TOP_SLOT = "4017014744";
const ADSENSE_RESULTS_BOTTOM_SLOT = "7055440733";

/* -------------------- helpers -------------------- */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[ResultsPage render error]", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="title-card" style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Erreur d’affichage</div>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8 }}>
            {String(this.state.error?.message || this.state.error)}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// const consent = useConsent();
// console.log("CONSENT", consent);

async function fetchJSONWithProgress(url, onProgress, signal) {
  onProgress?.({ step: "setup" });
  const res = await fetch(url, { cache: "no-store", signal });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {}

    const retryAfter = res.headers.get("retry-after");
    let message = detail || `HTTP ${res.status} ${res.statusText}`;
    if (res.status === 429) {
      message = `Trop de recherches ont été lancées. Réessayez dans ${retryAfter || "quelques"} secondes.`;
    } else if (res.status === 503) {
      message = `Le service est momentanément très sollicité. Réessayez dans ${retryAfter || "quelques"} secondes.`;
    } else if (res.status === 504) {
      message = "Le calcul du rapport a pris trop de temps. Vous pouvez réessayer dans quelques secondes.";
    }

    const error = new Error(message);
    error.status = res.status;
    throw error;
  }

  const len = parseInt(res.headers.get("content-length") || "0", 10);
  const canStream =
    !!res.body &&
    typeof res.body.getReader === "function" &&
    Number.isFinite(len) &&
    len > 0;

  if (!canStream) {
    onProgress?.({ step: "receiving" });
    const text = await res.text();
    onProgress?.({ step: "parsing" });
    const json = JSON.parse(text);
    onProgress?.({ step: "complete", progress: 100 });
    return json;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let received = 0;
  let text = "";

  onProgress?.({ step: "receiving", progress: 0 });
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    received += value.byteLength;
    text += decoder.decode(value, { stream: true });
    onProgress?.({ step: "receiving", progress: (received / len) * 100 });
  }

  text += decoder.decode();
  onProgress?.({ step: "parsing" });
  const json = JSON.parse(text);
  onProgress?.({ step: "complete", progress: 100 });
  return json;
}

function ProgressChip({ progress }) {
  if (!progress) return null;
  const label =
    progress.step === "setup"
      ? "Chargement des données… (~ 10 secondes max)"
      : progress.step === "receiving"
        ? "Chargement…"
        : progress.step === "parsing"
          ? "Analyse des données…"
          : progress.step === "complete"
            ? "Terminé !"
            : "Chargement…";
  const pct = progress.progress != null ? ` (${Math.round(progress.progress)}%)` : "";
  return (
    <div
      role="status"
      style={{
        fontSize: 12,
        color: "#64748b",
        padding: "4px 8px",
        background: "#f1f5f9",
        borderRadius: 6,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "2px solid rgba(100,116,139,.35)",
          borderTopColor: "#64748b",
          animation: "spin .9s linear infinite",
          display: "inline-block",
        }}
      />
      <span>
        {label}
        {pct}
      </span>
    </div>
  );
}

function ShareButton({ url, title, text, banId }) {
  const [status, setStatus] = React.useState("");

  async function copyToClipboard(value) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  async function handleShare() {
    let shareUrl = url || window.location.href;

    if (banId) {
      try {
        const response = await fetch("/api/shares", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ ban_id: banId }),
        });
        if (response.ok) {
          const share = await response.json();
          shareUrl = new URL(share.path, window.location.origin).toString();
          try {
            localStorage.setItem(`edl_share_revoke:${share.token}`, share.revoke_token);
          } catch {}
        }
      } catch {}
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        });
        return;
      }

      await copyToClipboard(shareUrl);
      setStatus("Lien copié");
    } catch (e) {
      if (e?.name === "AbortError") return;

      try {
        await copyToClipboard(shareUrl);
        setStatus("Lien copié");
      } catch {
        setStatus("Copie impossible");
      }
    }

    setTimeout(() => setStatus(""), 2500);
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        data-cta
        onClick={handleShare}
        title="Partager ce rapport"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 20px",
          fontSize: 16,
          fontWeight: 700,
          borderRadius: 14,
          border: "none",
          cursor: "pointer",
          color: "#fff",
          backgroundImage: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
          boxShadow: "0 8px 18px rgba(59,130,246,0.25)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>
          🔗
        </span>
        <span>Partager</span>
      </button>

      {status && (
        <span style={{ fontSize: 12, color: "#fff", fontWeight: 700 }}>
          {status}
        </span>
      )}
    </span>
  );
}

function SectionSkeleton({ lines = 4, min = 35, max = 95 }) {
  const bars = Array.from({ length: lines });
  return (
    <div className="chart-card skeleton">
      <div
        className="sk-line sk-title shimmer"
        style={{ width: "45%", height: 18, marginBottom: 10 }}
      />
      {bars.map((_, i) => {
        const w = Math.floor(Math.random() * (max - min + 1) + min);
        return (
          <div
            key={i}
            className="sk-line shimmer"
            style={{ width: `${w}%`, height: 12, margin: "10px 0" }}
          />
        );
      })}
      <div className="sk-block shimmer" style={{ height: 160, borderRadius: 12, marginTop: 8 }} />
    </div>
  );
}

const LS_KEY = "edl_history";
function readLocalHistory() {
  try {
    const raw = localStorage.getItem(LS_KEY) || "[]";
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeLocalHistory(list) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {}
}

export default function ResultsPage({ banIdOverride = "", forceNoIndex = false }) {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  const rawRouteId = banIdOverride || id || "";

  const qs = new URLSearchParams(location.search);
  const exampleMode = qs.get("example") === "1" || qs.get("example") === "true";
  const resolvedBanId = banIdOverride || (exampleMode ? rawRouteId : decodeBanIdFromUrl(rawRouteId));

  // Headless/explicit ?pdf=1 detection (kept for compatibility / print-like rendering)
  const pdfLike = React.useMemo(() => {
    try {
      const hasPdfQS = qs.has("pdf");
      const ua = navigator.userAgent || "";
      const webdriver = navigator.webdriver === true;
      const headlessUA = /\bHeadlessChrome\b|\bPlaywright\b|\bpuppeteer\b/i.test(ua);
      return hasPdfQS || webdriver || headlessUA;
    } catch {
      return false;
    }
  }, [qs]);

  const hideAuth = !user || pdfLike;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("adresse");
  const [errorMsg, setErrorMsg] = useState(null);
  const [progress, setProgress] = useState(null);

  const adsDisabled = exampleMode || pdfLike || !!data?.error;

  const TABS = [
    ["adresse", "Aperçu"],
    ["immobilier", "Immobilier"],
    ["meteo", "Météo"],
    ["climat", "Prévisions climatiques"],
    ["environnement", "Environnement"],
    ["risques", "Risques"],
    ["services", "Services"],
    ["culture", "Culture & Loisirs"],
    ["transports", "Transports"],
    ["sources", "Sources"],
  ];

  const TAB_COLORS = {
    adresse: "#e1bee7",
    immobilier: "#e3f2fd",
    meteo: "#fff9c4",
    climat: "#dec8a7",
    environnement: "#c8e6c9",
    risques: "#e6c8c8",
    services: "#ffe0b2",
    culture: "#c8e3e6",
    transports: "#d1c4e9",
    sources: "#eeeeee",
  };

  async function recordHistory(ban_id, label) {
    const payload = { ban_id, label: label || "" };
    try {
      await fetch("/api/me/history", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
    } catch {}

    const prev = readLocalHistory().filter((x) => x && x.ban_id !== ban_id);
    prev.unshift({ ...payload, ts: Date.now() });
    writeLocalHistory(prev.slice(0, 10));
  }

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let timedOut = false;
    let requestTimer = null;

    async function load() {
      if (!rawRouteId) return;
      setLoading(true);
      setErrorMsg(null);
      setProgress({ step: "setup" });

      try {
        if (exampleMode) {
          const cleanId = String(rawRouteId).replace(/[^a-zA-Z0-9._-]/g, "");
          const EXAMPLES = { example_full: exampleFull };
          const payload = EXAMPLES[cleanId] || EXAMPLES["example_full"];

          if (!payload) {
            const msg = `Aucun exemple trouvé pour ${cleanId}`;
            setErrorMsg(msg);
            setData({ error: msg, free: false });
          } else {
            const copy = JSON.parse(JSON.stringify(payload));
            copy.free = false;
            setData(copy);
          }

          setProgress(null);
          setLoading(false);
          return;
        }

        const path = `/api/address/dvf_data/${encodeURIComponent(resolvedBanId)}`;
        requestTimer = window.setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, 25000);
        const payload = await fetchJSONWithProgress(
          path,
          (nextProgress) => {
            if (active) setProgress(nextProgress);
          },
          controller.signal
        );

        if (!active) return;

        try {
          if (payload && typeof payload === "object") payload.free = false;
        } catch {}

        setData(payload);
      } catch (e) {
        if (!active || (e?.name === "AbortError" && !timedOut)) return;
        const msg = timedOut
          ? "Le chargement du rapport a dépassé 25 secondes. Vous pouvez réessayer."
          : String(e?.message || e);
        setErrorMsg(msg);
        setData({ error: msg });
        console.error("[ResultsPage] load() failed:", msg);
      } finally {
        if (requestTimer) window.clearTimeout(requestTimer);
        if (active) {
          setLoading(false);
          setProgress(null);
        }
      }
    }

    load();
    return () => {
      active = false;
      if (requestTimer) window.clearTimeout(requestTimer);
      controller.abort();
    };
  }, [rawRouteId, resolvedBanId, exampleMode]);

  useEffect(() => {
    if (data) {
      window.__DICTIMMO_CURRENT_DATA__ = data;
      window.__DICTIMMO_CURRENT_ID__ = resolvedBanId || null;
    }
  }, [data, resolvedBanId]);

  const communeName = deriveCommuneName(data);
  const deptCode = extractDeptFromInsee(data?.code_insee);
  const deptName =
    deptCode && DEPT_DICT[deptCode] ? DEPT_DICT[deptCode] : deptCode || "Département";

  const addressLabel =
    data?.adresse?.label ??
    (typeof data?.ban_id === "string"
      ? data.ban_id.split(" ").slice(0, -2).join(" ")
      : undefined) ??
    "";

  useEffect(() => {
    if (!data || !resolvedBanId || exampleMode) return;
    const bid = typeof data?.ban_id === "string" ? data.ban_id : resolvedBanId;
    if (!bid) return;
    recordHistory(bid, addressLabel);
  }, [data, resolvedBanId, exampleMode, addressLabel]);

  const activeTabColor = TAB_COLORS[tab] || "#ffffff";

  const hexToRGBA = (hex, a = 0.22) => {
    const h = hex.replace("#", "").trim();
    const val =
      h.length === 3
        ? h.split("").map((c) => parseInt(c + c, 16))
        : [
            parseInt(h.slice(0, 2), 16),
            parseInt(h.slice(2, 4), 16),
            parseInt(h.slice(4, 6), 16),
          ];
    if (val.some((v) => Number.isNaN(v))) return `rgba(0,0,0,${a})`;
    const [r, g, b] = val;
    return `rgba(${r},${g},${b},${a})`;
  };

  // Canonical origin handling
  const preferredOrigin = "https://etatdulieu.com";
  const runtimeOrigin =
    (typeof window !== "undefined" && window.location?.origin) || preferredOrigin;
  const canonicalOrigin = runtimeOrigin.includes("://www.etatdulieu.com")
    ? preferredOrigin
    : runtimeOrigin;

  const cleanBanId = (typeof data?.ban_id === "string" ? data.ban_id : resolvedBanId) || "";
  const publicResultId = encodeBanIdForUrl(cleanBanId);
  const canonicalUrl = `${canonicalOrigin}/results/${encodeURIComponent(publicResultId)}`;
  const isExample = exampleMode;
  const YEAR = new Date().getFullYear();
  const defaultOgImage = `${canonicalOrigin}/og/default-address.png`;
  const dynamicOgImage = `${canonicalOrigin}/og/address.png?ban_id=${encodeURIComponent(cleanBanId)}`;
  const ogImage = dynamicOgImage || defaultOgImage;

  const pageTitle = data
    ? `Rapport d’adresse • ${addressLabel || cleanBanId} (${communeName || "Commune"}) – État du Lieu`
    : `Rapport d’adresse – État du Lieu`;

  const pageDescription = data
    ? `Rapport ${YEAR} pour ${addressLabel || cleanBanId} (${communeName || "commune"}${deptName ? `, ${deptName}` : ""}) : prix immobiliers, météo, démographie, électricité, élections, crimes, risques, services et transports.`
    : `Rapport ${YEAR} d’une adresse en France : prix immo, météo, démographie, risques, services et transports.`;

  const GlobalStyle = () => (
    <style>{`
      :root{
        --bg1:#3b82f6; --bg2:#1e40af;
        --surface:rgba(255,255,255,0.95);
        --ink:#0f172a; --muted:#64748b;
        --skel:#e5e7eb; --rail:#e5e7eb;
        --container-pad:16px;
        --auth-reserve: 88px;
        --tabs-rail-inset: 12px;
      }
      html, body {
        margin: 0;
        padding: 0;
        color-scheme: light;
      }
      .results-page{
        min-height:100vh;
        background: linear-gradient(135deg,var(--bg1) 0%, var(--bg2) 100%);
        font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,sans-serif;
        color:var(--ink);
        position: relative;
      }
      .r-container{
        max-width:1080px;
        margin:0 auto;
        padding: calc(28px + var(--auth-reserve)) var(--container-pad) 64px;
      }
      .actions-row{
        display:flex; align-items:center; justify-content:space-between;
        gap:12px; margin-bottom: 8px;
      }
      .glass-chip{
        background: var(--surface);
        backdrop-filter: blur(18px);
        border: 1px solid rgba(255,255,255,0.22);
        border-radius: 14px;
        padding: 8px 10px;
        box-shadow: 0 12px 28px rgba(0,0,0,0.12);
        display: inline-flex;
        align-items:center;
        gap:10px;
      }
      .back-chip a{
        text-decoration:none;
        color:var(--ink);
        font-weight:600;
      }
      .floating-action{
        display:flex;
        gap:8px;
        flex-wrap:wrap;
        filter: drop-shadow(0 10px 24px rgba(0,0,0,.28));
      }
      .results-page .floating-action > button,
      .results-page .floating-action > a,
      .results-page button[data-cta]{
        min-height:44px;
        padding:14px 20px;
        font-size:16px;
        font-weight:600;
        border-radius:14px;
      }
      .results-page .header-auth .btn{
        padding:8px 12px !important;
        min-height:auto !important;
        font-size:14px !important;
        border-radius:10px !important;
      }
      .hero{
        display:flex;
        align-items:center;
        justify-content:center;
        margin: 2px 0 12px;
      }
      .hero-title-wrap{
        display:inline-flex;
        align-items:center;
        gap:12px;
      }
      .hero-logo{
        height:clamp(52px, 3em, 80px);
        width:auto;
        display:block;
      }
      .hero-title{
        font-size: clamp(34px, 6.2vw, 56px);
        font-weight: 900;
        letter-spacing:.2px;
        color:#fff;
        text-align:center;
        line-height:1.03;
        text-shadow: 0 3px 18px rgba(0,0,0,.28);
        margin:0;
      }
      .title-card{
        background: var(--surface);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255,255,255,0.22);
        border-radius: 18px;
        padding: 14px 16px;
        margin: 6px 0 18px;
        box-shadow: 0 16px 36px rgba(0,0,0,0.12);
      }

      .adsense-wrap{
        max-width: 980px;
        margin: 10px auto 18px;
        padding: 10px 12px 12px;
        border: 1px solid rgba(255,255,255,0.22);
        border-radius: 18px;
        background: rgba(255,255,255,0.96);
        backdrop-filter: blur(16px);
        box-shadow: 0 14px 32px rgba(0,0,0,0.12);
        overflow: hidden;
      }
      .adsense-label{
        font-size: 11px;
        line-height: 1;
        color: var(--muted);
        text-transform: uppercase;
        letter-spacing: .08em;
        font-weight: 700;
        margin-bottom: 10px;
      }
      .adsense-slot{
        width: 100%;
        overflow: hidden;
      }
      .adsense-wrap--top{
        margin-top: 10px;
        margin-bottom: 16px;
      }
      .adsense-wrap--top .adsense-slot{
        min-height: 72px;
      }
      .adsense-wrap--bottom{
        margin-top: 28px;
        margin-bottom: 24px;
      }
      .adsense-wrap--bottom .adsense-slot{
        min-height: 110px;
      }

      .tabs{
        display: grid;
        gap: 12px;
      }
      @media (min-width: 641px){
        .tabs { gap: 0; }
        .tab-list{
          display:flex;
          align-items:flex-end;
          gap:8px;
          padding: 0 var(--container-pad);
          margin: 0 0 -1px 0;
          background: transparent;
          border: 0;
          position: relative;
        }
        .tab-list::after{
          content:"";
          position:absolute;
          left: var(--tabs-rail-inset);
          right: var(--tabs-rail-inset);
          bottom:0;
          height:1px;
          background: var(--rail);
          z-index: 1;
          border-radius: 999px;
        }
        .tab-button{
          position: relative;
          z-index: 2;
          border: 1px solid rgba(0,0,0,0.10);
          border-bottom: none;
          border-radius: 12px 12px 0 0;
          padding: 10px 14px 8px;
          background:#f3f4f6;
          color:#0f172a !important;
          cursor:pointer;
          white-space:nowrap;
          box-shadow: 0 2px 6px rgba(0,0,0,.05);
          transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
          font-weight: 600;
          transform-origin: bottom center;
        }
        .tab-button:hover{
          filter: brightness(0.98);
          box-shadow: 0 3px 10px rgba(0,0,0,.08);
        }
        .tab-button[aria-selected="true"]{
          transform: scale(1.06);
          z-index: 3;
          box-shadow: 0 10px 22px rgba(0,0,0,0.12);
          font-weight: 900;
          background: var(--active-tab-bg, #f3f4f6) !important;
          border-color: var(--active-tab-bg, #f3f4f6) !important;
          background-image: none !important;
        }
        .tab-button:first-child{ border-top-left-radius: 18px; }
        .tab-button:last-child{  border-top-right-radius: 18px; }
      }
      .tab-mobile{
        display: none;
      }
      .tab-mobile .select-wrap{
        position: relative;
        border: 2px solid transparent;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 8px 20px rgba(0,0,0,.08);
      }
      .tab-mobile .tab-select{
        width: 100%;
        appearance: none;
        -webkit-appearance: none;
        border: 0;
        border-radius: 12px;
        padding: 10px 40px 10px 34px;
        font-size: 15px;
        color: #0f172a;
        background: transparent;
        outline: none;
      }
      .tab-mobile .tab-select:focus{
        outline: 3px solid var(--active-tab-bg, #a3a3a3);
        outline-offset: 2px;
      }
      .tab-mobile .caret{
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        font-size: 16px;
        color: #64748b;
      }
      .tab-panel{ display:none; }
      .tab-panel.is-active{ display:block; }

      .chart-card{
        border:1px solid rgba(255,255,255,0.22);
        border-radius:12px;
        background: var(--surface);
        backdrop-filter: blur(18px);
        padding:12px;
        box-shadow: 0 14px 32px rgba(0,0,0,0.12);
      }
      .skeleton{ position: relative; overflow: hidden; }
      .sk-line, .sk-block{
        background: linear-gradient(90deg, var(--skel) 25%, #f3f4f6 37%, var(--skel) 63%);
        background-size: 400% 100%;
        animation: shimmer 1.2s ease-in-out infinite;
        border-radius: 6px;
      }
      .sk-title{ border-radius: 10px; }
      .tab-pill{
        height: 30px;
        width: 110px;
        border-radius: 999px;
        background: linear-gradient(90deg, var(--skel) 25%, #f3f4f6 37%, var(--skel) 63%);
        background-size: 400% 100%;
        animation: shimmer 1.2s ease-in-out infinite;
      }
      @keyframes shimmer{
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes spin{
        to { transform: rotate(360deg); }
      }
      .grid-2{
        display:grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }
      .grid-3{
        display:grid;
        grid-template-columns: repeat(3,1fr);
        gap: 16px;
      }
      .chart-flex{
        display:flex;
        gap:16px;
        align-items:flex-start;
        flex-wrap:wrap;
      }
      .chart-right{
        flex:1 1 280px;
        min-width:240px;
      }
      svg{
        max-width:100%;
        height:auto;
        display:block;
      }

      @media (max-width: 768px){
        .r-container{
          padding-top: calc(20px + 88px);
        }
        .grid-2, .grid-3{
          grid-template-columns: 1fr;
        }
        .chart-flex{
          flex-direction: column;
        }
        .chart-right{
          min-width: 0;
          width: 100%;
        }
        .title-card{
          padding: 12px;
        }
      }
      @media (max-width: 640px){
        :root, html, body, .results-page{
          color-scheme: light;
          background-color: #ffffff;
        }

        .actions-row{
          flex-wrap: wrap;
          align-items: flex-start;
          row-gap: 8px;
        }
        .actions-row [role="status"]{
          flex: 1 1 auto;
          min-width: 140px;
        }

        .adsense-wrap{
          padding: 8px 10px 10px;
          border-radius: 14px;
        }
        .adsense-wrap--top .adsense-slot{
          min-height: 56px;
        }
        .adsense-wrap--bottom .adsense-slot{
          min-height: 96px;
        }

        .tab-list{
          display: none !important;
        }
        .tab-mobile{
          display: block !important;
          margin: 2px 0 8px;
          z-index: 1;
        }

        .actions-row .floating-action{
          width:100%;
          justify-content:flex-end;
          flex-wrap: wrap;
        }
        .actions-row .floating-action > *{
          flex: 0 0 auto;
        }
        .floating-action :where(button, a){
          white-space: normal !important;
          overflow-wrap: anywhere;
          line-height: 1.12;
        }
      }
      @media (max-width: 420px){
        .actions-row .floating-action{
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
      }
      @media (max-width: 640px) and (prefers-color-scheme: dark){
        .title-card,
        .glass-chip,
        .chart-card,
        .tab-mobile .select-wrap,
        .adsense-wrap{
          background: #ffffff !important;
          color: #0f172a !important;
          border-color: rgba(0,0,0,0.12) !important;
        }
      }
      @media (max-width: 400px){
        .back-chip a{ font-size: 12px; }
      }

      @media print{
        .results-page{
          background:#fff !important;
        }
        .actions-row,
        .hero,
        .adsense-wrap{
          display:none !important;
        }
        .tab-list,
        .tab-mobile{
          display: none !important;
        }
        .tab-panel{
          display: block !important;
        }
        .tab-panel + .tab-panel{
          break-before: page;
          page-break-before: always;
        }
      }
    `}</style>
  );

  const LoadingChrome = () => (
    <>
      <div className="actions-row">
        <div className="glass-chip back-chip">
          <Link to="/">← Retour</Link>
        </div>
        <span className="floating-action" aria-hidden="true" />
      </div>
      <div className="hero" aria-hidden="true">
        <div className="sk-line shimmer" style={{ width: "60%", height: 42, borderRadius: 10 }} />
      </div>
      <div className="title-card skeleton" aria-busy="true" aria-live="polite">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <ProgressChip progress={progress} />
        </div>
        <div className="sk-line sk-title shimmer" style={{ width: "55%", height: 22, marginBottom: 12 }} />
        <div className="sk-line shimmer" style={{ width: "30%", height: 14 }} />
      </div>
      <div className="tab-list" role="presentation" aria-hidden="true">
        <div className="tab-pill" />
        <div className="tab-pill" />
        <div className="tab-pill" />
        <div className="tab-pill" />
      </div>
      <div className="grid-2" style={{ marginTop: 12 }}>
        <SectionSkeleton />
        <SectionSkeleton />
      </div>
    </>
  );

  const onOpenLogin = () =>
    window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "login" } }));
  const onOpenSignup = () =>
    window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "signup" } }));

  const pageStyle = {
    position: "relative",
    ...(hideAuth ? { ["--auth-reserve"]: "0px" } : {}),
  };

  // ===== Loading state =====
  if (loading) {
    return (
      <div className="results-page" style={pageStyle}>
        <Helmet>
          <title>Chargement du rapport… – État du Lieu</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        {!hideAuth && (
          <HeaderAuth
            hideWhenGuest
            floating
            top={12}
            right={12}
            onOpenLogin={onOpenLogin}
            onOpenSignup={onOpenSignup}
          />
        )}

        <GlobalStyle />

        <div className="r-container">
          <LoadingChrome />
          <SiteFooter />
        </div>
      </div>
    );
  }

  // ===== Not found / no data =====
  if (!data) {
    return (
      <div className="results-page" style={pageStyle}>
        <Helmet>
          <title>Adresse introuvable – État du Lieu</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        {!hideAuth && (
          <HeaderAuth
            hideWhenGuest
            floating
            top={12}
            right={12}
            onOpenLogin={onOpenLogin}
            onOpenSignup={onOpenSignup}
          />
        )}

        <GlobalStyle />

        <div className="r-container">
          <div className="actions-row">
            <div className="glass-chip back-chip">
              <Link to="/">← Retour</Link>
            </div>
            <span className="floating-action" />
          </div>

          <div className="hero">
            <div className="hero-title-wrap">
              <img src={logo} alt="État du Lieu" className="hero-logo" />
              <h1 className="hero-title">État du Lieu</h1>
            </div>
          </div>

          <div className="title-card">Adresse non trouvée</div>

          <SiteFooter />
        </div>
      </div>
    );
  }

  // ===== Normal page =====
  const metrics = data.metrics || {};

  return (
    <div className="results-page" style={pageStyle}>
      <Helmet prioritizeSeoTags>
        {!isExample && <link rel="canonical" href={canonicalUrl} />}
        <meta name="robots" content={(isExample || forceNoIndex) ? "noindex, nofollow" : "noindex, follow"} />

        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="État du Lieu" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {!isExample && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content={ogImage} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {!hideAuth && (
        <HeaderAuth
          hideWhenGuest
          floating
          top={12}
          right={12}
          onOpenLogin={onOpenLogin}
          onOpenSignup={onOpenSignup}
        />
      )}

      <GlobalStyle />

      <div className="r-container">
        <div className="actions-row" role="navigation" aria-label="Actions">
          <div className="glass-chip back-chip">
            <Link to="/" aria-label="Revenir à la recherche">
              ← Retour
            </Link>
          </div>

          <ProgressChip progress={progress} />

          <span className="floating-action">
            <ShareButton
              url={canonicalUrl}
              banId={cleanBanId}
              title={pageTitle}
              text={
                addressLabel
                  ? `Rapport État du Lieu pour ${addressLabel}`
                  : "Rapport État du Lieu"
              }
            />

            <FavoriteButton banId={data?.ban_id || cleanBanId} label={addressLabel} />
          </span>
        </div>

        <AdSenseBanner
          slot={ADSENSE_RESULTS_TOP_SLOT}
          variant="top"
          disabled={adsDisabled}
        />

        <div className="hero">
          <div className="hero-title-wrap">
            <img src={logo} alt="État du Lieu" className="hero-logo" />
            <h1 className="hero-title">État du Lieu</h1>
          </div>
        </div>

        <div className="title-card" aria-live="polite">
          <h2 style={{ marginTop: 0 }}>
            État du lieu :{" "}
            {typeof data.ban_id === "string"
              ? data.ban_id.split(" ").slice(0, -2).join(" ")
              : data.ban_id}
          </h2>

          <div style={{ color: "var(--muted)", marginTop: 6 }}>
            {communeName}
            {deptName ? ` • ${deptName}` : ""}
          </div>

          {typeof errorMsg === "string" && errorMsg.trim() && (
            <div
              style={{
                marginTop: 10,
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#7f1d1d",
                borderRadius: 8,
                padding: 10,
                whiteSpace: "pre-wrap",
              }}
            >
              <strong>Erreur de chargement du rapport :</strong>
              {"\n"}
              {errorMsg}
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  style={{
                    border: "1px solid #b91c1c",
                    borderRadius: 7,
                    background: "#ffffff",
                    color: "#991b1b",
                    padding: "7px 11px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Réessayer
                </button>
              </div>
            </div>
          )}
        </div>

        <ErrorBoundary>
          <div
            className="tabs"
            aria-label="Sections du rapport"
            style={{ "--active-tab-bg": activeTabColor }}
          >
            <div className="tab-list" role="tablist" aria-label="Rapport">
              {TABS.map(([key, label]) => {
                const selected = tab === key;
                const bg = TAB_COLORS[key] || "#ffffff";

                return (
                  <button
                    key={key}
                    id={`tab-${key}`}
                    className="tab-button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={key}
                    onClick={() => setTab(key)}
                    style={
                      selected
                        ? {
                            backgroundColor: bg,
                            borderColor: bg,
                            backgroundImage: "none",
                          }
                        : undefined
                    }
                    title={label}
                  >
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>

            <div
              className="tab-mobile"
              role="navigation"
              aria-label="Sections (mobile)"
              style={{ "--active-tab-bg": activeTabColor }}
            >
              <div
                className="select-wrap"
                style={{
                  border: `2px solid ${activeTabColor}`,
                  background: `linear-gradient(135deg, ${hexToRGBA(activeTabColor, 0.26)}, ${hexToRGBA(activeTabColor, 0.12)})`,
                }}
              >
                <label
                  htmlFor="tab-select"
                  className="sr-only"
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0,0,0,0)",
                    border: 0,
                  }}
                >
                  Choisir une section
                </label>

                <select
                  id="tab-select"
                  className="tab-select"
                  value={tab}
                  onChange={(e) => setTab(e.target.value)}
                  aria-label="Choisir une section du rapport"
                >
                  {TABS.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>

                <span className="caret" aria-hidden="true">
                  ▾
                </span>
              </div>
            </div>

            <section
              id="adresse"
              className={`tab-panel ${tab === "adresse" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-adresse"
              style={{ "--hdr-bg": "#e1bee7", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <AdresseSection data={data} deptName={deptName} />
              </Suspense>
            </section>

            <section
              id="immobilier"
              className={`tab-panel ${tab === "immobilier" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-immobilier"
              style={{ "--hdr-bg": "#e3f2fd", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <ImmobilierSection
                  data={data}
                  TYPES={["Maison", "Appartement"]}
                  TARGET_YEAR={"2025"}
                  sectionMetrics={metrics.section || {}}
                  communeMetrics={metrics.commune || {}}
                  deptMetrics={metrics.departement || {}}
                  paysMetrics={metrics.pays || {}}
                  communeName={communeName}
                  deptName={deptName}
                  nfmt={nfmt}
                  nfmt2={nfmt2}
                />
              </Suspense>
            </section>

            <section
              id="meteo"
              className={`tab-panel ${tab === "meteo" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-meteo"
              style={{ "--hdr-bg": "#fff9c4", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <MeteoSection
                  data={data}
                  communeName={communeName}
                  deptName={deptName}
                  DEPT_DICT={DEPT_DICT}
                />
              </Suspense>
            </section>

            <section
              id="climat"
              className={`tab-panel ${tab === "climat" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-climat"
              style={{ "--hdr-bg": "#dec8a7", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <ClimatSection data={data} deptName={deptName} />
              </Suspense>
            </section>

            <section
              id="environnement"
              className={`tab-panel ${tab === "environnement" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-environnement"
              style={{ "--hdr-bg": "#c8e6c9", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <EnvironnementSection data={data} />
              </Suspense>
            </section>

            <section
              id="risques"
              className={`tab-panel ${tab === "risques" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-risques"
              style={{ "--hdr-bg": "#e6c8c8", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <RisksSection data={data} defaultYear={"2024"} />
              </Suspense>
            </section>

            <section
              id="services"
              className={`tab-panel ${tab === "services" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-services"
              style={{ "--hdr-bg": "#ffe0b2", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <ServicesSection data={data} />
              </Suspense>
            </section>

            <section
              id="culture"
              className={`tab-panel ${tab === "culture" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-culture"
              style={{ "--hdr-bg": "#c8e3e6", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <CultureSection data={data} />
              </Suspense>
            </section>

            <section
              id="transports"
              className={`tab-panel ${tab === "transports" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-transports"
              style={{ "--hdr-bg": "#d1c4e9", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <TransportsSection data={data} />
              </Suspense>
            </section>

            <section
              id="sources"
              className={`tab-panel ${tab === "sources" ? "is-active" : ""} print-section`}
              role="tabpanel"
              aria-labelledby="tab-sources"
              style={{ "--hdr-bg": "#eeeeee", "--hdr-fg": "#111827" }}
            >
              <Suspense fallback={<SectionSkeleton />}>
                <SourcesSection data={data} />
              </Suspense>
            </section>
          </div>
        </ErrorBoundary>

        {/* Bottom mobile tab selector */}
        <div
          className="tab-mobile"
          role="navigation"
          aria-label="Sections (mobile, bas de page)"
          style={{ "--active-tab-bg": activeTabColor, marginTop: 28 }}
        >
          <div
            className="select-wrap"
            style={{
              border: `2px solid ${activeTabColor}`,
              background: `linear-gradient(135deg, ${hexToRGBA(activeTabColor, 0.26)}, ${hexToRGBA(activeTabColor, 0.12)})`,
            }}
          >
            <label
              htmlFor="tab-select-bottom"
              className="sr-only"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                border: 0,
              }}
            >
              Choisir une section
            </label>

            <select
              id="tab-select-bottom"
              className="tab-select"
              value={tab}
              onChange={(e) => setTab(e.target.value)}
              aria-label="Choisir une section du rapport"
            >
              {TABS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            <span className="caret" aria-hidden="true">
              ▾
            </span>
          </div>
        </div>

        <AdSenseBanner
          slot={ADSENSE_RESULTS_BOTTOM_SLOT}
          variant="bottom"
          disabled={adsDisabled}
        />

        <SiteFooter />
      </div>
    </div>
  );
}
